import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { CalendarDays, Upload, Link2, Users, FileText, Sparkles, Loader2, X, List, LayoutGrid, Settings, Coffee, Ban, Clock, Sun, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../../styles/LuminaCalendar.css';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { useAppMode } from '../../contexts/AppModeContext';

const localizer = momentLocalizer(moment);

const DEMO_APPOINTMENTS = [
  { id: 'APT-001', patientId: 'P001', patient: 'John Smith', time: '9:00 AM', duration: '30 min', type: 'Follow-up', chief: 'Diabetes management', status: 'completed', credits: 10 },
  { id: 'APT-002', patientId: 'P002', patient: 'Maria Garcia', time: '9:30 AM', duration: '45 min', type: 'New Patient', chief: 'Annual physical', status: 'in_progress', credits: 15 },
  { id: 'APT-003', patientId: 'P003', patient: 'Robert Johnson', time: '10:00 AM', duration: '20 min', type: 'Sick Visit', chief: 'Cough and fever', status: 'scheduled', credits: 7 },
  { id: 'APT-004', patientId: 'P004', patient: 'Emily Davis', time: '10:30 AM', duration: '30 min', type: 'Follow-up', chief: 'HTN follow-up', status: 'scheduled', credits: 10 },
  { id: 'APT-005', patientId: 'P005', patient: 'Sarah Williams', time: '11:00 AM', duration: '30 min', type: 'Follow-up', chief: 'Asthma check', status: 'scheduled', credits: 10 },
  { id: 'APT-006', patientId: 'P001', patient: 'James Taylor', time: '1:00 PM', duration: '45 min', type: 'New Patient', chief: 'Chest pain evaluation', status: 'scheduled', credits: 15 },
];

const LuminaAppointments = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [appointments] = useState(DEMO_APPOINTMENTS);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('day');
  const [showSettings, setShowSettings] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState({
    officeStart: '08:00', officeEnd: '17:00',
    lunchStart: '12:00', lunchEnd: '13:00',
    lunchEnabled: true, showClosed: true,
  });

  const SCHEDULE_BLOCKS = useMemo(() => {
    const today = new Date();
    const blocks = [];
    // Lunch block
    if (scheduleSettings.lunchEnabled) {
      const [lh, lm] = scheduleSettings.lunchStart.split(':').map(Number);
      const [le, lem] = scheduleSettings.lunchEnd.split(':').map(Number);
      blocks.push({
        id: 'BLOCK-LUNCH', title: 'Lunch Break',
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate(), lh, lm),
        end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), le, lem),
        resource: { id: 'BLOCK-LUNCH', type: 'block', blockType: 'lunch', status: 'block' },
      });
    }
    // Office Closed (before open)
    if (scheduleSettings.showClosed) {
      const [oh, om] = scheduleSettings.officeStart.split(':').map(Number);
      blocks.push({
        id: 'BLOCK-BEFORE', title: 'Office Closed',
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 7, 0),
        end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), oh, om),
        resource: { id: 'BLOCK-BEFORE', type: 'block', blockType: 'closed', status: 'block' },
      });
      // After close
      const [ch, cm] = scheduleSettings.officeEnd.split(':').map(Number);
      blocks.push({
        id: 'BLOCK-AFTER', title: 'Office Closed',
        start: new Date(today.getFullYear(), today.getMonth(), today.getDate(), ch, cm),
        end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 19, 0),
        resource: { id: 'BLOCK-AFTER', type: 'block', blockType: 'closed', status: 'block' },
      });
    }
    // No Patients block (2-2:30 gap)
    blocks.push({
      id: 'BLOCK-NOPATIENTS', title: 'No Patients / Admin Time',
      start: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 11, 30),
      end: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0),
      resource: { id: 'BLOCK-NOPATIENTS', type: 'block', blockType: 'no_patients', status: 'block' },
    });
    return blocks;
  }, [scheduleSettings]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  const handleProcessSchedule = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setShowUpload(false);
      setUploadedFile(null);
      toast.success('Schedule imported', { description: '6 appointments added for today' });
    }, 2000);
  };

  // Convert appointments to calendar events
  const calendarEvents = useMemo(() => {
    const today = new Date();
    return appointments.map(apt => {
      const [time, period] = apt.time.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 += 12;
      if (period === 'AM' && hours === 12) hour24 = 0;

      const durMinutes = parseInt(apt.duration) || 30;
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour24, minutes);
      const end = new Date(start.getTime() + durMinutes * 60000);

      return {
        id: apt.id,
        title: `${apt.patient} — ${apt.type}`,
        start,
        end,
        resource: apt,
      };
    });
  }, [appointments]);

  const allCalendarEvents = useMemo(() => [...calendarEvents, ...SCHEDULE_BLOCKS], [calendarEvents, SCHEDULE_BLOCKS]);

  const handleCalendarEventClick = (event) => {
    if (event.resource.type === 'block') return;
    navigate(`/lumina/patients/${event.resource.patientId}`);
  };

  const eventStyleGetter = (event) => {
    const status = event.resource.status;
    if (event.resource.type === 'block') {
      const blockColors = {
        lunch: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
        closed: { bg: '#f1f5f9', border: '#94a3b8', text: '#475569' },
        no_patients: { bg: '#fce7f3', border: '#ec4899', text: '#831843' },
        day_off: { bg: '#f0fdf4', border: '#22c55e', text: '#166534' },
      };
      const c = blockColors[event.resource.blockType] || blockColors.closed;
      return {
        style: {
          backgroundColor: c.bg, borderLeft: `4px solid ${c.border}`,
          borderTop: 'none', borderRight: 'none', borderBottom: 'none',
          borderRadius: '8px', color: c.text, fontSize: '12px', fontWeight: 600,
          padding: '4px 10px', opacity: 0.85, fontStyle: 'italic',
        }
      };
    }
    const colors = {
      completed: { bg: '#ede9fe', border: '#7c3aed', text: '#4c1d95' },
      in_progress: { bg: '#ddd6fe', border: '#8b5cf6', text: '#4c1d95' },
      scheduled: { bg: '#e0e7ff', border: '#6366f1', text: '#312e81' },
    };
    const c = colors[status] || colors.scheduled;
    return {
      style: {
        backgroundColor: c.bg,
        borderLeft: `4px solid ${c.border}`,
        borderTop: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        borderRadius: '8px',
        color: c.text,
        fontSize: '13px',
        fontWeight: 600,
        padding: '4px 10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        lineHeight: '1.4',
      }
    };
  };

  const handleNavigate = useCallback((date) => setCalendarDate(date), []);
  const handleViewChange = useCallback((view) => setCalendarView(view), []);

  const totalEstCredits = appointments.reduce((s, a) => s + a.credits, 0);
  const completed = appointments.filter(a => a.status === 'completed').length;

  const getStatusBadge = (status) => {
    const map = {
      scheduled: { label: 'Scheduled', cls: 'bg-slate-100 text-slate-600' },
      in_progress: { label: 'In Progress', cls: 'bg-violet-100 text-violet-700' },
      completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700' },
    };
    const c = map[status] || map.scheduled;
    return <Badge className={cn("text-xs", c.cls)}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Appointments</h1>
          <p className="text-slate-500 mt-1">Today's patient schedule &middot; {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              className={cn("rounded-none gap-1.5", viewMode === 'list' && "bg-black text-white")}
              onClick={() => setViewMode('list')}
              data-testid="view-list-btn"
            >
              <List className="h-4 w-4" /> List
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className={cn("rounded-none gap-1.5", viewMode === 'calendar' && "bg-black text-white")}
              onClick={() => setViewMode('calendar')}
              data-testid="view-calendar-btn"
            >
              <LayoutGrid className="h-4 w-4" /> Calendar
            </Button>
          </div>
          <Button variant="outline" className="gap-2" onClick={() => setShowUpload(true)} data-testid="upload-schedule-btn">
            <Upload className="h-4 w-4" /> Upload Schedule
          </Button>
          <Button variant="outline" className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => { toast.info('Lens enrichment', { description: 'Upload clinical docs to auto-route to patient charts' }); setShowUpload(true); }} data-testid="enrich-lens-btn">
            <Camera className="h-4 w-4" /> Enrich via Lens
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => navigate('/lumina/integrations')} data-testid="sync-ehr-btn">
            <Link2 className="h-4 w-4" /> Sync from EHR
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowSettings(true)} data-testid="schedule-settings-btn">
            <Settings className="h-4 w-4" /> Schedule Settings
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Appointments</p><p className="text-2xl font-bold mt-1">{appointments.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Completed</p><p className="text-2xl font-bold text-violet-600 mt-1">{completed}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Remaining</p><p className="text-2xl font-bold text-amber-600 mt-1">{appointments.length - completed}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Est. Credits Needed</p><p className="text-2xl font-bold mt-1">{totalEstCredits}</p></CardContent></Card>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card data-testid="calendar-view">
          <CardContent className="p-4">
            {/* Block Legend */}
            <div className="flex items-center gap-4 mb-3 text-[11px]">
              <span className="text-muted-foreground font-medium">Blocks:</span>
              {[
                { label: 'Lunch', color: 'bg-amber-200 border-amber-400' },
                { label: 'No Patients', color: 'bg-pink-200 border-pink-400' },
                { label: 'Office Closed', color: 'bg-slate-200 border-slate-400' },
              ].map(b => (
                <div key={b.label} className="flex items-center gap-1.5">
                  <div className={cn("h-3 w-6 rounded border", b.color)} />
                  <span className="text-muted-foreground">{b.label}</span>
                </div>
              ))}
            </div>
            <div style={{ height: 520 }}>
              <Calendar
                localizer={localizer}
                events={allCalendarEvents}
                startAccessor="start"
                endAccessor="end"
                view={calendarView}
                onView={handleViewChange}
                date={calendarDate}
                onNavigate={handleNavigate}
                views={['day', 'week']}
                min={new Date(0, 0, 0, 7, 0)}
                max={new Date(0, 0, 0, 19, 0)}
                step={15}
                timeslots={2}
                onSelectEvent={handleCalendarEventClick}
                eventPropGetter={eventStyleGetter}
                style={{ height: '100%' }}
                popup
                formats={{
                  eventTimeRangeFormat: () => null,
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Today's Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {appointments.map((apt) => (
              <div
                key={apt.id}
                className={cn(
                  "flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all hover:shadow-sm",
                  apt.status === 'in_progress' ? "border-violet-200 bg-violet-50/30" : "hover:border-slate-300"
                )}
                onClick={() => navigate(`/lumina/patients/${apt.patientId}`)}
                data-testid={`appointment-${apt.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="text-center w-16">
                    <p className="text-sm font-bold text-slate-900">{apt.time}</p>
                    <p className="text-xs text-muted-foreground">{apt.duration}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{apt.patient}</p>
                    <p className="text-xs text-muted-foreground">{apt.type} &middot; {apt.chief}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={(e) => { e.stopPropagation(); toast.info('Upload clinical docs', { description: `Upload docs for ${apt.patient}` }); }} className="h-7 w-7 rounded-lg border border-amber-200 bg-amber-50 flex items-center justify-center hover:bg-amber-100 transition-colors" title={`Upload clinical docs for ${apt.patient}`} data-testid={`lens-${apt.id}`}>
                    <Camera className="h-3.5 w-3.5 text-amber-600" />
                  </button>
                  <span className="text-xs text-muted-foreground">~{apt.credits} credits</span>
                  {getStatusBadge(apt.status)}
                  {apt.status === 'in_progress' && (
                    <Button size="sm" className="bg-black hover:bg-gray-800 h-7 text-xs">Continue Visit</Button>
                  )}
                  {apt.status === 'scheduled' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/lumina/patients/${apt.patientId}`); }}>
                      Start Visit
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upload Schedule Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md" data-testid="upload-schedule-modal">
          <DialogHeader>
            <DialogTitle>Upload Appointment Schedule</DialogTitle>
            <DialogDescription>Upload a screenshot, photo, PDF, or exported report of your daily schedule</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!uploadedFile ? (
              <label className="block">
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-violet-400 hover:bg-violet-50/20 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600">Drop your schedule here or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Supports PNG, JPG, PDF</p>
                </div>
                <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleFileUpload} data-testid="schedule-file-input" />
              </label>
            ) : (
              <div className="flex items-center justify-between rounded-lg border bg-slate-50 p-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-violet-600" />
                  <span className="text-sm font-medium">{uploadedFile.name}</span>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setUploadedFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-violet-600 mt-0.5" />
                <p className="text-xs text-violet-700">Lumina's AI will analyze your schedule and automatically create today's appointment list with patient names, times, and visit types.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUpload(false); setUploadedFile(null); }}>Cancel</Button>
            <Button onClick={handleProcessSchedule} disabled={!uploadedFile || processing} className="bg-black hover:bg-gray-800 gap-2" data-testid="process-schedule-btn">
              {processing ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</> : <><Sparkles className="h-4 w-4" /> Extract Schedule</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md" data-testid="schedule-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold flex items-center gap-2"><Settings className="h-5 w-5" /> Schedule Settings</DialogTitle>
            <DialogDescription>Configure office hours, breaks, and schedule blocks</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Sun className="h-4 w-4 text-amber-500" /> Office Hours</h4>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Opens At</Label><Input type="time" value={scheduleSettings.officeStart} onChange={(e) => setScheduleSettings(s => ({ ...s, officeStart: e.target.value }))} data-testid="office-start-input" /></div>
                <div><Label className="text-xs">Closes At</Label><Input type="time" value={scheduleSettings.officeEnd} onChange={(e) => setScheduleSettings(s => ({ ...s, officeEnd: e.target.value }))} data-testid="office-end-input" /></div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Coffee className="h-4 w-4 text-amber-600" /> Lunch Break</h4>
                <Switch checked={scheduleSettings.lunchEnabled} onCheckedChange={(v) => setScheduleSettings(s => ({ ...s, lunchEnabled: v }))} data-testid="lunch-toggle" />
              </div>
              {scheduleSettings.lunchEnabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Start</Label><Input type="time" value={scheduleSettings.lunchStart} onChange={(e) => setScheduleSettings(s => ({ ...s, lunchStart: e.target.value }))} data-testid="lunch-start-input" /></div>
                  <div><Label className="text-xs">End</Label><Input type="time" value={scheduleSettings.lunchEnd} onChange={(e) => setScheduleSettings(s => ({ ...s, lunchEnd: e.target.value }))} data-testid="lunch-end-input" /></div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ban className="h-4 w-4 text-slate-500" />
                <div><p className="text-sm font-medium">Show Closed Blocks</p><p className="text-[10px] text-muted-foreground">Display before/after hours as office closed</p></div>
              </div>
              <Switch checked={scheduleSettings.showClosed} onCheckedChange={(v) => setScheduleSettings(s => ({ ...s, showClosed: v }))} data-testid="closed-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button className="bg-black hover:bg-gray-800" onClick={() => { setShowSettings(false); toast.success('Schedule settings saved'); }} data-testid="save-settings-btn">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaAppointments;
